import { consentDecided, onConsentDecided } from '@/marketing/consent';
import { hasConsent, type ConsentCategory } from '@/shared/consent';
import { api } from '@shared/api';

import { currentLang } from './lang';
import { trackLauncherLift } from './lift';

import './support-chat.css';

/**
 * Module-level, and set before the first await.
 *
 * `window.chatwootSDK` only appears once the script has downloaded, so guarding on
 * it leaves the whole config request unprotected: two calls in that window would
 * append two scripts and run the widget twice. Only `main.tsx` calls this today, so
 * this is the invariant rather than a live bug — and it is the same hazard the
 * dashboard keeps a dedicated flag for.
 */
let initialised = false;

/**
 * Distinct from `initialised`, which means "a boot ATTEMPT owns the flow".
 * `booted` means run() actually happened — the grant branch of the consent
 * watcher needs the difference: after a boot attempt that died at the config
 * request, re-granting used to hit `toggleBubbleVisibility` on a widget that
 * never existed and the real boot became unreachable for the session.
 */
let booted = false;

/** The two locale listeners are page-lifetime; armed on the first boot that
 *  passes the allow-list and never again. */
let localeListenersArmed = false;

/** The lift observer's disposer — installed once a widget exists to keep clear,
 *  torn down when consent is withdrawn and there is no launcher left to place. */
let liftStop: (() => void) | null = null;

/** `cc:onChange` is watched once; the flag, not the listener, carries the state. */
let consentWatched = false;

/**
 * Withdrawal, and the change of mind after a decline — neither arrives through
 * `onConsentDecided`, which fires on the FIRST decision only. The library
 * dispatches `cc:onChange` on every later save, so this is where both paths
 * live:
 *
 * - **granted later** (declined at the banner, accepted from the footer): boot.
 *   The old wiring left no listener after a decline, so this path was dead and
 *   the comment claiming otherwise was wrong.
 * - **withdrawn**: the same reasoning that made the cookie declinable makes it
 *   withdrawable. The panel is closed, `reset()` drops the `cw_conversation`
 *   cookie and the contact with it, and the launcher hides. The SDK itself
 *   cannot be unloaded, but it is inert: no conversation, no visible surface,
 *   and the socket of a reset widget carries an empty contact.
 */
function watchConsent(): void {
  if (consentWatched || typeof window === 'undefined') return;
  consentWatched = true;
  window.addEventListener('cc:onChange', () => {
    if (hasConsent(SUPPORT_CHAT_CATEGORY)) {
      if (booted) {
        liftStop ??= trackLauncherLift();
        try {
          window.$chatwoot?.toggleBubbleVisibility?.('show');
        } catch {
          // Half-booted; the launcher reappears on the next boot path.
        }
      } else {
        void initSupportChat();
      }
      return;
    }
    if (!booted) return;
    liftStop?.();
    liftStop = null;
    try {
      window.$chatwoot?.toggle?.('close');
      window.$chatwoot?.reset?.();
      window.$chatwoot?.toggleBubbleVisibility?.('hide');
    } catch {
      // Not ready to tear down; the cookie the visitor withdrew from is only
      // written by a widget that finished loading, which is the one this reaches.
    }
  });
}

export { setSupportChatLanguage } from './language';

interface SupportChatConfig {
  base_url: string;
  website_token: string;
}

/** Chatwoot serves its widget SDK from the instance itself. */
const SDK_PATH = '/packs/js/sdk.js';

/**
 * Support chat on the marketing site (DIT-336).
 *
 * Imperative and client-only, for the same reason the consent banner is: every
 * marketing route is prerendered by `vite-react-ssg`, so a React component would
 * either bake a not-yet-loaded widget into the static HTML or mismatch on
 * hydration. Chatwoot renders its own DOM after load instead.
 *
 * **Anonymous by definition.** There is no session on the marketing site, so no
 * identity is asserted and no identity hash is requested — a visitor here is a
 * prospect, and Chatwoot's pre-chat form takes a name and email. That also means
 * this can run before anyone has signed up, which is the point of putting it here
 * at all: the site was not a surface in the original decision.
 *
 * Failure is silent by design. The config endpoint answers 503 where no inbox is
 * deployed, and a marketing page must not show an error — or a launcher that opens
 * onto nothing — because a support chat could not start.
 */
/**
 * The category the chat is declared under, and therefore the one a visitor can
 * decline. `preferences` rather than `necessary`: the conversation token remembers a
 * thread across visits, which is a convenience, not something the site cannot run
 * without.
 */
const SUPPORT_CHAT_CATEGORY: ConsentCategory = 'preferences';

/**
 * Give up on this attempt, leaving nothing behind that costs anything.
 *
 * `initialised` is released so a later entry can try again, and the lift tracker
 * is torn down — it was installed once the config answered, and every abandon
 * path after that point used to leave it attached: a MutationObserver on
 * `documentElement` with `subtree: true, attributes: true`, plus capture-phase
 * transitionend/animationend and a resize listener, each waking a measure that
 * forces layout, on a site running scroll and motion libraries. The comment at
 * the install site moved it there precisely so a 503 environment would not
 * measure for the whole session; an SDK 404 or a withdrawal mid-download put it
 * straight back.
 */
function standDown(): void {
  initialised = false;
  liftStop?.();
  liftStop = null;
}

/**
 * How many boots have been abandoned, and the point at which we stop trying.
 *
 * The retry is keyed on the pathname, which is what makes "recoverable on the next
 * entry" true — and uncapped, it also makes a permanently blocked SDK cost a config
 * request and a dead `<script>` node on every page view. An ad-blocker is the
 * ordinary case for a chat widget, so that is twenty of each in a twenty-page visit.
 *
 * Three: enough that a transient 503 or a dropped request heals on the next
 * navigation, few enough that a blocked host stops costing anything. Not persisted —
 * a reload is a new session and deserves a fresh look.
 *
 * ⚠ Counted for every abandon, not only a failed script load. Incremented in
 * `script.onerror` alone, it capped the dead `<script>` nodes and left the other
 * half of the cost uncapped — and the uncapped half is the commoner one: an
 * environment with no inbox, or one whose endpoint 503s, returns before a script is
 * ever injected, so it kept issuing one config request per navigation for the whole
 * visit. Every give-up goes through {@link giveUp} now.
 *
 * Consent is deliberately NOT counted. A visitor who has not answered the banner, or
 * who withdrew, is a state that legitimately changes back, and spending the budget
 * on it would mean a grant after three page views could never boot.
 */
let abandoned = 0;
const MAX_BOOT_ATTEMPTS = 3;

/**
 * Abandon this attempt and spend one of the budget.
 *
 * `standDown()` on its own is the consent-shaped exit: release the latch, keep the
 * budget. This is the failure-shaped one.
 */
function giveUp(): void {
  abandoned += 1;
  standDown();
}

/**
 * Start the widget on an SDK that is already loaded, and record that it is up.
 *
 * `run()` is wrapped because a throw here was the one abandon path that did not
 * honour the initialised/booted contract: the flag stays true, `booted` never
 * becomes true, and every later entry — the consent watcher's grant branch and the
 * per-navigation retry alike — returns immediately at the latch. Called from
 * `script.onload` it is not even a rejection `void initSupportChat()` could carry,
 * just an error on the window. Blocked storage and an SDK init failure both reach
 * it.
 */
function startWidget(config: SupportChatConfig): void {
  try {
    window.chatwootSDK?.run({
      websiteToken: config.website_token,
      baseUrl: config.base_url,
    });
  } catch (error) {
    console.warn('[support-chat] the SDK refused to start', error);
    giveUp();
    return;
  }
  // Only now is there a widget: the consent watcher's grant branch shows an
  // existing launcher when this is true and boots from scratch when it is not.
  booted = true;
}

/** Boot the support chat — see the module doc above for the shape and the why. */
export async function initSupportChat(): Promise<void> {
  if (initialised) return;
  // Given up on for this page load. See MAX_BOOT_ATTEMPTS: the retry runs on every
  // navigation, so without a ceiling a blocked SDK costs a config request and a dead
  // script node per page view for the whole visit.
  if (abandoned >= MAX_BOOT_ATTEMPTS) return;

  if (typeof window === 'undefined') return; // prerender pass
  // `booted`, NOT `window.chatwootSDK`. The global is set the instant the script
  // executes, which is BEFORE we decide whether to run the widget — so a
  // withdrawal landing during the download turned every later re-entry into an
  // immediate return here, with no launcher, no run() and no console line. The
  // whole `initialised` / `booted` split exists to keep a recoverable failure
  // recoverable; the SDK-download gap was the one case that still escaped it.
  if (booted) return; // already booted

  // Nothing loads until the visitor has answered the banner, and then only if they
  // accepted the category this chat sits in.
  //
  // The previous version ran unconditionally beside `initConsent()`, so Chatwoot set
  // its cookie, opened a websocket and transmitted the visitor's address and page URL
  // before any choice existed to record. Declaring the cookie as strictly necessary
  // made that legal on paper and took the choice away in practice — a circle this
  // breaks from the other side: wait for the answer, and the chat can be something a
  // visitor is allowed to decline.
  watchConsent();
  if (!consentDecided()) {
    const stop = onConsentDecided(() => {
      stop();
      void initSupportChat();
    });
    return;
  }
  if (!hasConsent(SUPPORT_CHAT_CATEGORY)) return;

  initialised = true;

  let config: SupportChatConfig;
  try {
    const { data } = await api.get<SupportChatConfig>('/v4/support-chat/config/', {
      // For the request log only, and knowingly inert beyond it: the API reads
      // `platform` when building custom attributes, which exist for authenticated
      // callers, and this caller never is one. Kept because a log line that says
      // which surface booted is worth one query parameter.
      params: { platform: 'landing' },
    });
    if (!data?.website_token || !data.base_url) {
      // No inbox in this environment. Releasing the latch matters even here: a
      // later consent grant re-enters, and an environment can gain an inbox
      // between two visits — a latched flag turned that into a dead session. But
      // it spends the budget: "no inbox" is the environment's steady state, not a
      // blip, and re-asking on every navigation for the whole visit buys nothing.
      giveUp();
      return;
    }
    config = data;
  } catch {
    // 503, offline, blocked — recoverable on the next entry, but bounded: this is
    // the request Anton's note was actually about, and it was the one still
    // repeating per navigation.
    giveUp();
    return;
  }

  // The visitor may have answered the preferences dialog while the request was
  // in flight — the withdrawal branch of the consent watcher had nothing to
  // tear down yet (no widget exists), so the recheck lives here, before
  // anything observable happens.
  if (!hasConsent(SUPPORT_CHAT_CATEGORY)) {
    initialised = false;
    return;
  }

  // Only now that there is a widget to keep clear: the observer watches every DOM
  // mutation of an animated site, and installing it before the config answered
  // left it measuring for the whole session on environments whose endpoint 503s.
  liftStop ??= trackLauncherLift();

  // Chatwoot bakes the launcher copy at `run()`, so this is the value that counts —
  // and `currentLang()` now asks the site rather than guessing, so it is right at boot
  // even for a visitor landing on `/`.
  // The allow-list runs before anything observable: refused, the boot must be a
  // clean no-op — the earlier order left chatwootSettings written and two window
  // listeners attached, accumulating a fresh pair on every re-entry.
  //
  // The value comes from our own API, but it is interpolated into a <script src>
  // that then runs on every one of ~488 prerendered pages — so a compromised or
  // misconfigured SUPPORT_CHAT_BASE_URL would be arbitrary script execution across
  // the site rather than a broken widget. The first version compared the SDK URL's
  // origin against the base URL's own origin, which is a tautology (the former is
  // derived from the latter) and let any https host through. The real check: the
  // page's own origin (the CloudFront-fronted same-host case, and the e2e stub), or
  // a support host under our apex over https — nothing else, and a refusal leaves a
  // console line to find.
  let src: URL;
  try {
    src = new URL(SDK_PATH, config.base_url);
  } catch {
    console.warn('[support-chat] base_url is not a valid URL');
    giveUp();
    return;
  }
  const OUR_SUPPORT_HOST = /^support(\.[a-z0-9-]+)*\.done-it\.app$/;
  const allowed =
    src.origin === window.location.origin ||
    (src.protocol === 'https:' && OUR_SUPPORT_HOST.test(src.hostname));
  if (!allowed) {
    console.warn('[support-chat] refusing SDK from unexpected origin', src.origin);
    giveUp();
    return;
  }

  window.chatwootSettings = { position: 'right', type: 'expanded_bubble', locale: currentLang() };

  // Kept as a correction rather than as the mechanism: it cannot repaint copy
  // already baked at run(). `chatwoot:ready` covers the boot race;
  // `languagechange` covers the browser/OS list changing. The site's own switcher
  // is neither — it is a client-side navigate — and reaches the widget through
  // `setSupportChatLanguage`, called from the router's locale gate. Armed once:
  // re-entries after a released latch must not stack listener pairs.
  if (!localeListenersArmed) {
    localeListenersArmed = true;
    const applyLocale = () => window.$chatwoot?.setLocale(currentLang());
    window.addEventListener('chatwoot:ready', applyLocale);
    window.addEventListener('languagechange', applyLocale);
  }

  // The SDK may already be in the document from an earlier attempt that was
  // turned away after it loaded — a withdrawal in the download gap, most likely.
  // Injecting a second copy would be pointless and would stack another onload.
  if (window.chatwootSDK?.run) {
    startWidget(config);
    return;
  }

  const script = document.createElement('script');
  script.src = src.toString();
  script.async = true;
  script.defer = true;
  script.onload = () => {
    // The SDK download is the second await-shaped gap a withdrawal can land in.
    if (!hasConsent(SUPPORT_CHAT_CATEGORY)) {
      standDown();
      return;
    }
    if (!window.chatwootSDK?.run) {
      // The script loaded and the global is not what we expect — a version that
      // renamed it, or something else served from that path. Latching `initialised`
      // here would keep the chat off for the rest of the session with nothing said.
      console.warn('[support-chat] SDK loaded but exposes no run()');
      giveUp();
      return;
    }
    startWidget(config);
  };
  script.onerror = () => {
    // Blocked, offline, or the host is down. Nothing user-facing — a marketing page
    // must not show an error for a chat that could not start — but the latch is
    // released so a later navigation can try again, and there is a line to find.
    console.warn('[support-chat] SDK failed to load');
    // And the element goes with it. Left in place, the retry appended a new one per
    // navigation and `head` filled with dead nodes pointing at a host that is not
    // answering.
    script.remove();
    giveUp();
  };
  document.head.appendChild(script);
}

declare global {
  interface Window {
    chatwootSDK?: { run: (opts: { websiteToken: string; baseUrl: string }) => void };
    $chatwoot?: {
      setLocale: (locale: string) => void;
      reset?: () => void;
      toggle?: (state?: 'open' | 'close') => void;
      toggleBubbleVisibility?: (state: 'show' | 'hide') => void;
    };
    chatwootSettings?: Record<string, unknown>;
  }
}
